/**
 * scan-controle.js — de tussenstap na het herkennen van een gescande veldwerkopdracht.
 *
 * Eerst ging de herkende tekst meteen door naar de omzetting tot veldwerkopdracht. Dan zie je
 * nooit wat er gelezen is, en een verkeerd gelezen projectnummer of telefoonnummer kom je pas
 * tegen als het al in de opdracht staat. Nu zie je links de pagina's en rechts de tekst; die
 * kun je verbeteren voordat je hem laat verwerken.
 *
 *   const tekst = await controleerScan({ text, paginas, bestandsnaam });
 *   if (tekst == null) return;   // geannuleerd
 *
 * Zelfde opzet als confirm-dialog.js: een element op <body> dat zichzelf opruimt.
 */
import { LitElement, html, css } from 'lit';

export class ScanControleDialog extends LitElement {
  static properties = {
    tekst: { type: String },
    paginas: { attribute: false },
    bestandsnaam: { type: String },
    _open: { state: true },
    _pagina: { state: true },
  };

  constructor() {
    super();
    this.tekst = '';
    this.paginas = [];
    this.bestandsnaam = '';
    this._open = false;
    this._pagina = 0;
    this._resolve = null;
    this._onKey = (e) => { if (e.key === 'Escape') this._sluit(null); };
  }

  /** @returns {Promise<string|null>} de (eventueel verbeterde) tekst, of null bij annuleren */
  show() {
    document.addEventListener('keydown', this._onKey);
    requestAnimationFrame(() => { this._open = true; });
    return new Promise((resolve) => { this._resolve = resolve; });
  }

  _sluit(uitkomst) {
    if (!this._resolve) return;
    const resolve = this._resolve;
    this._resolve = null;
    document.removeEventListener('keydown', this._onKey);
    this._open = false;
    setTimeout(() => { resolve(uitkomst); this.remove(); }, 200);
  }

  _verwerk() {
    const tekst = this.renderRoot.querySelector('textarea')?.value ?? this.tekst;
    if (tekst.trim().length < 40) return;
    this._sluit(tekst);
  }

  render() {
    const n = this.paginas.length;
    return html`
      <div class="overlay ${this._open ? 'open' : ''}" @click=${(e) => { if (e.target === e.currentTarget) this._sluit(null); }}>
        <div class="venster" role="dialog" aria-modal="true" aria-label="Herkende tekst controleren">
          <header>
            <div>
              <h2>Herkende tekst controleren</h2>
              <span>${this.bestandsnaam}${n ? ` · ${n} ${n === 1 ? 'pagina' : "pagina's"}` : ''}</span>
            </div>
            <button class="sluit" @click=${() => this._sluit(null)} aria-label="Sluiten">&times;</button>
          </header>
          <p class="uitleg">
            Dit is de tekst die uit de scan is gelezen. Klopt er iets niet, verbeter het hier; daarna
            maken we er een veldwerkopdracht van.
          </p>
          <div class="lijf ${n ? '' : 'zonder'}">
            ${n ? html`
              <div class="scan">
                <img src=${this.paginas[this._pagina]} alt=${`Pagina ${this._pagina + 1}`}>
                ${n > 1 ? html`
                  <div class="bladeren">
                    ${this.paginas.map((_, i) => html`
                      <button class=${i === this._pagina ? 'aan' : ''} @click=${() => { this._pagina = i; }}>${i + 1}</button>`)}
                  </div>` : ''}
              </div>` : ''}
            <textarea spellcheck="false" .value=${this.tekst}></textarea>
          </div>
          <footer>
            <button class="knop" @click=${() => this._sluit(null)}>Annuleren</button>
            <button class="knop primair" @click=${this._verwerk}>Verwerken tot veldwerkopdracht</button>
          </footer>
        </div>
      </div>`;
  }

  static styles = css`
    .overlay { position:fixed; inset:0; z-index:10000; display:flex; align-items:center; justify-content:center; padding:20px;
               background:rgba(0,0,0,.4); backdrop-filter:blur(4px); opacity:0; pointer-events:none; transition:opacity .2s ease; }
    .overlay.open { opacity:1; pointer-events:auto; }
    .venster { width:100%; max-width:1100px; height:min(88vh, 900px); display:flex; flex-direction:column;
               background:var(--mv-surface,#fff); border-radius:14px; box-shadow:0 24px 70px rgba(0,0,0,.25); overflow:hidden;
               transform:translateY(20px) scale(.97); transition:transform .3s cubic-bezier(.16,1,.3,1); }
    .overlay.open .venster { transform:none; }
    header { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding:18px 22px 6px; }
    h2 { margin:0; font-size:1.12rem; color:var(--mv-text,#181c32); }
    header span { font-size:.8rem; color:var(--mv-text-muted,#a1a5b7); }
    .sluit { border:none; background:none; font-size:1.5rem; line-height:1; color:var(--mv-text-muted,#a1a5b7); cursor:pointer; padding:0; }
    .uitleg { margin:0; padding:0 22px 12px; font-size:.88rem; line-height:1.5; color:var(--mv-text-sub,#5e6278); }
    .lijf { flex:1; min-height:0; display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:14px; padding:0 22px; }
    .lijf.zonder { grid-template-columns:minmax(0,1fr); }
    .scan { min-height:0; display:flex; flex-direction:column; gap:8px; }
    .scan img { flex:1; min-height:0; width:100%; object-fit:contain; object-position:top; border:1px solid var(--mv-border,#eff2f5);
                border-radius:8px; background:var(--mv-surface-alt,#f8f9fa); }
    .bladeren { display:flex; gap:4px; flex-wrap:wrap; }
    .bladeren button { min-width:30px; height:28px; border:1px solid var(--mv-border,#eff2f5); border-radius:6px; background:var(--mv-surface,#fff);
                       font:inherit; font-size:.8rem; font-weight:600; color:var(--mv-text-sub,#5e6278); cursor:pointer; }
    .bladeren button.aan { border-color:var(--mv-primary,#4b9d35); color:var(--mv-primary,#4b9d35); }
    textarea { min-height:0; width:100%; height:100%; box-sizing:border-box; resize:none; padding:12px 14px;
               border:1px solid var(--mv-border,#eff2f5); border-radius:8px; background:var(--mv-surface,#fff);
               font:13px/1.55 ui-monospace,SFMono-Regular,Menlo,monospace; color:var(--mv-text,#181c32); }
    textarea:focus { outline:none; border-color:var(--mv-primary,#4b9d35); }
    footer { display:flex; justify-content:flex-end; gap:.75rem; padding:16px 22px 18px; }
    .knop { padding:.5rem 1.1rem; border-radius:6px; font-size:.95rem; font-weight:500; cursor:pointer;
            background:var(--mv-surface-alt,#f8f9fa); color:var(--mv-text-sub,#5e6278); border:1px solid var(--mv-border,#eff2f5); }
    .knop.primair { background:var(--mv-primary,#4b9d35); border-color:var(--mv-primary,#4b9d35); color:#fff; }
    .knop.primair:hover { background:var(--mv-primary-hover,#3d812b); }
    @media (max-width: 760px) { .lijf { grid-template-columns:1fr; grid-template-rows:200px minmax(0,1fr); } }
  `;
}

customElements.define('scan-controle-dialog', ScanControleDialog);

/**
 * De herkende tekst laten controleren.
 * @returns {Promise<string|null>} de tekst om te verwerken, of null als de gebruiker annuleert
 */
export function controleerScan({ text, paginas = [], bestandsnaam = '' }) {
  const el = document.createElement('scan-controle-dialog');
  el.tekst = text || '';
  el.paginas = paginas;
  el.bestandsnaam = bestandsnaam;
  document.body.appendChild(el);
  return el.show();
}
